import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { context as viewportContext } from 'yola-editor/src/js/utils/viewport-provider';
import { Modal, designSystem } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import getPagePreview from '../helpers/get-page-preview';
import fieldNames from '../constants/field-names';

const {
  Heading,
  Paragraph,
  Box,
  Stack,
  ActionButton,
  Divider,
  SimpleSelectField,
  FieldStatus,
  InputGroupField,
  CheckboxField,
  ControlGroup,
  Tag,
  BetaLabel,
  TextAreaField,
} = designSystem;

const DEFAULT_WIDTH = 812;
const TABLET_BREAKPOINT = 576;

const defaultCaptions = {};
const defaultPageOptions = [];

function AddPageDialog({
  captions = defaultCaptions,
  pageOptions = defaultPageOptions,
  selectedOption = null,
  onCancel = null,
  onSubmit,
  onPageOptionChange = Function.prototype,
  showPreviewLabel = false,
  control,
  fields,
  formErrors,
}) {
  const { width } = useContext(viewportContext);
  const isSmallScreen = bowser.mobile || width <= TABLET_BREAKPOINT;
  const Preview = getPagePreview(selectedOption.value);
  const { label: textAreaLabel, statusText: textAreaStatusText } =
    fields[fieldNames.USER_INPUT] || {};
  const withTextArea = Boolean(textAreaLabel);

  const classes = classnames('ws-add-page-dialog', {
    'ws-add-page-dialog--mobile-view': isSmallScreen,
  });

  const submitButtonPaddings = {
    paddingTop: isSmallScreen ? 'spacing-s' : 'spacing-xs',
    paddingBottom: isSmallScreen ? 'spacing-s' : 'spacing-xs',
    paddingLeft: isSmallScreen ? 'spacing-m' : 'spacing-xl',
    paddingRight: isSmallScreen ? 'spacing-m' : 'spacing-xl',
  };

  const contentContainerPaddings = {
    paddingTop: isSmallScreen ? 'spacing-m' : 'spacing-3-xl',
    paddingBottom: isSmallScreen ? 'spacing-m' : 'spacing-l',
    paddingLeft: isSmallScreen ? 'spacing-m' : 'spacing-xl',
    paddingRight: isSmallScreen ? 'spacing-m' : 'spacing-xl',
  };

  return (
    <Modal
      width={DEFAULT_WIDTH}
      height="auto"
      overlay="visible"
      overlaySpacing={{
        x: 16,
        y: 16,
      }}
      centered
      fullscreen={isSmallScreen}
      draggable={false}
      resizable={false}
      isContainerScrollable
      handleCancel={onCancel}
    >
      <div className={classes}>
        <div className="ws-add-page-dialog__preview-container">
          {onCancel && isSmallScreen && (
            <div className="ws-add-page-dialog__close-button">
              <ActionButton onClick={onCancel} format="text" iconGlyph="close" />
            </div>
          )}
          <div className="ws-add-page-dialog__image-container-wrapper">
            <div className="ws-add-page-dialog__image-container">
              {showPreviewLabel && (
                <div className="ws-add-page-dialog__label-container">
                  <Tag label={captions.previewLabel} iconGlyph="ai" />
                </div>
              )}

              <Preview />
            </div>
          </div>
        </div>

        <div className="ws-add-page-dialog__content-container">
          {onCancel && !isSmallScreen && (
            <div className="ws-add-page-dialog__close-button">
              <ActionButton onClick={onCancel} format="text" iconGlyph="close" />
            </div>
          )}
          <Box {...contentContainerPaddings}>
            <Heading type="heading-3">
              {captions.heading}
              {showPreviewLabel && (
                <span>
                  {' '}
                  <BetaLabel />
                </span>
              )}
            </Heading>

            <Box marginTop="spacing-3-xs">
              <Paragraph>{captions.description}</Paragraph>
            </Box>

            <Box marginTop="spacing-m">
              <Stack gap="spacing-3-xs">
                <SimpleSelectField
                  name={fieldNames.PAGE_TEMPLATE_SELECT}
                  control={control}
                  options={pageOptions}
                  onChange={onPageOptionChange}
                />
                {captions.pageTemplateSubtitle && (
                  <Paragraph appearance="medium-emphasis" size="small">
                    {captions.pageTemplateSubtitle}
                  </Paragraph>
                )}
              </Stack>
            </Box>

            <Box marginTop="spacing-xs">
              <ControlGroup title={captions.inputTitle} required>
                <InputGroupField
                  id={fieldNames.PAGE_TEMPLATE_SELECT}
                  name={fieldNames.PAGE_NAME_INPUT}
                  control={control}
                  rules={fields[fieldNames.PAGE_NAME_INPUT].rules}
                />
              </ControlGroup>
            </Box>

            {withTextArea && fields[fieldNames.USER_INPUT] && (
              <Box marginTop="spacing-xs">
                <ControlGroup title={textAreaLabel}>
                  <TextAreaField
                    name={fieldNames.USER_INPUT}
                    control={control}
                    rules={fields[fieldNames.USER_INPUT].rules}
                    resize
                  />
                  {textAreaStatusText && !formErrors[fieldNames.USER_INPUT]?.message && (
                    <FieldStatus text={textAreaStatusText} />
                  )}
                </ControlGroup>
              </Box>
            )}

            <Box marginTop="spacing-xs">
              <CheckboxField
                id={fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX}
                name={fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX}
                label={captions.checkboxTitle}
                control={control}
                value={fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX}
              />
            </Box>
          </Box>

          <div className="ws-add-page-dialog__submit-button-container-wrapper">
            <Divider />
            <Box {...submitButtonPaddings}>
              <div className="ws-add-page-dialog__submit-button-container">
                <ActionButton
                  appearance="accent"
                  format="solid"
                  iconGlyph={showPreviewLabel ? 'ai' : null}
                  label={captions.button}
                  size="large"
                  fullWidth={isSmallScreen}
                  onClick={onSubmit}
                />
              </div>
            </Box>
          </div>
        </div>
      </div>
    </Modal>
  );
}

AddPageDialog.propTypes = {
  onCancel: PropTypes.func,
  onPageOptionChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  captions: PropTypes.shape({
    heading: PropTypes.string,
    description: PropTypes.string,
    button: PropTypes.string,
    pageTemplateSubtitle: PropTypes.string,
    inputTitle: PropTypes.string,
    checkboxTitle: PropTypes.string,
    previewLabel: PropTypes.string,
  }),
  pageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  selectedOption: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  showPreviewLabel: PropTypes.bool,
  fields: PropTypes.shape().isRequired,
  control: PropTypes.shape().isRequired,
  formErrors: PropTypes.shape().isRequired,
};

export default AddPageDialog;
